/**
 * Asymmetric cryptography from the `e2e-crypto` kit: per-user identity (ECDH
 * P-256 key pair) and ECIES-style "sealing to public key". Canon
 * Boveda/P-3 (ZERO_KNOWLEDGE_VAULT §3.4).
 *
 * Usage in Bodega: distributing the team key of a BYOS remote to each member
 * of the organization (the key is encrypted to the member's PUBLIC KEY; only
 * their private key decrypts it). The sender does not need the recipient's
 * private key: forward secrecy per envelope thanks to an ephemeral key pair per
 * operation.
 *
 * ECIES(P-256):
 *   epk = ephemeral key pair;  shared = ECDH(epk.priv, recipient.pub)
 *   wrapKey = HKDF(shared, info = "…" || epk.pub)   (binds the wrap key to epk)
 *   envelope = AES-256-GCM(wrapKey, payload, aad)
 *   transmits { epk.pub, envelope };  the recipient recomputes shared with their priv.
 */

import { base64ToBytes, bytesToBase64, concatBytes } from "./encoding";
import { aeadOpen, aeadSeal, hkdfExpand, importAesKey } from "./primitives";

const CURVE = "P-256";

export interface KeyPairRaw {
  /** Public key in raw format (65 bytes, base64). Public: not a secret. */
  readonly publicKey: string;
  /** Private key in PKCS#8 (base64). SECRET: wrapped with the passphrase. */
  readonly privateKey: string;
}

export interface SealedToPublicKey {
  /** Ephemeral public key (raw, base64). */
  readonly epk: string;
  /** AES-GCM envelope (base64). */
  readonly env: string;
}

function ab(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

export async function generateKeyPair(): Promise<KeyPairRaw> {
  const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: CURVE }, true, [
    "deriveBits",
  ]);
  const pub = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  const priv = new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey));
  return { publicKey: bytesToBase64(pub), privateKey: bytesToBase64(priv) };
}

async function importPublic(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", ab(raw), { name: "ECDH", namedCurve: CURVE }, true, []);
}

async function importPrivate(pkcs8: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", ab(pkcs8), { name: "ECDH", namedCurve: CURVE }, false, [
    "deriveBits",
  ]);
}

async function ecdh(priv: CryptoKey, pub: CryptoKey): Promise<Uint8Array> {
  const bits = await crypto.subtle.deriveBits({ name: "ECDH", public: pub }, priv, 256);
  return new Uint8Array(bits);
}

/** Seals `payload` (typically a team key, 32 bytes) to `publicKeyB64`. */
export async function sealToPublicKey(
  publicKeyB64: string,
  payload: Uint8Array,
  aad: string,
): Promise<SealedToPublicKey> {
  const recipientPub = await importPublic(base64ToBytes(publicKeyB64));
  const ephemeral = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: CURVE }, true, [
    "deriveBits",
  ]);
  const epkRaw = new Uint8Array(await crypto.subtle.exportKey("raw", ephemeral.publicKey));
  const shared = await ecdh(ephemeral.privateKey, recipientPub);
  const wrapRaw = await hkdfExpand(concatBytes(shared, epkRaw), `bodega:ecies:v1:${aad}`);
  const wrapKey = await importAesKey(wrapRaw, ["encrypt"]);
  const env = await aeadSeal(wrapKey, payload, aad);
  return { epk: bytesToBase64(epkRaw), env: bytesToBase64(env) };
}

export async function openWithPrivateKey(
  privateKeyB64: string,
  sealed: SealedToPublicKey,
  aad: string,
): Promise<Uint8Array> {
  const priv = await importPrivate(base64ToBytes(privateKeyB64));
  const epkRaw = base64ToBytes(sealed.epk);
  const epk = await importPublic(epkRaw);
  const shared = await ecdh(priv, epk);
  const wrapRaw = await hkdfExpand(concatBytes(shared, epkRaw), `bodega:ecies:v1:${aad}`);
  const wrapKey = await importAesKey(wrapRaw, ["decrypt"]);
  return aeadOpen(wrapKey, base64ToBytes(sealed.env), aad);
}
